import styled from 'styled-components';
import type {
  ThemeType,
} from '@n3/css-base';

import { buttonSizes } from './buttonSizes';
import { buttonColors } from './buttonColors';
import type {
  ButtonSize,
  ButtonColor,
} from './types';

export type StyledButtonProps = {
  $color?: ButtonColor;
  $size?: ButtonSize;
  disabled?: boolean;
  $isLoading?: boolean;
  $isPressed?: boolean;
  $isFlatLeft?: boolean;
  $isFlatRight?: boolean;
  $isOnlyIcon?: boolean;
};

export const getMinHeight = (size: ButtonSize, theme: ThemeType) => {
  switch (size) {
    case 'default':
      return theme.button.MIN_HEIGHT;

    case 'large':
      return theme.button.MIN_HEIGHT_LARGE;

    case 'small':
      return theme.button.MIN_HEIGHT_SMALL;

    default:
      throw new Error(`[@n3/kit] Unknown size of button "${size as string}"`);
  }
};

const getColors = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
      return theme.button.COLORS_BUTTON_DANGER[0];

    case 'danger_tertiary':
      return theme.button.COLORS_BUTTON_DANGER_TERTIARY[0];

    case 'danger_ghost':
      return theme.button.COLORS_BUTTON_DANGER_GHOST[0];

    case 'primary_ghost':
      return theme.button.COLORS_BUTTON_PRIMARY_GHOST[0];

    case 'default':
      return theme.button.COLORS_BUTTON_SECONDARY[0];

    case 'tertiary':
      return theme.button.COLORS_BUTTON_TERTIARY[0];

    case 'ghost':
      return theme.button.COLORS_BUTTON_GHOST[0];

    case 'primary':
      return theme.button.COLORS_BUTTON_PRIMARY[0];

    default:
      return theme.button.COLORS_BUTTON_SECONDARY[0];
  }
};

const getColorsHover = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
      return theme.button.COLORS_BUTTON_DANGER[1];

    case 'danger_tertiary':
      return theme.button.COLORS_BUTTON_DANGER_TERTIARY[1];

    case 'danger_ghost':
      return theme.button.COLORS_BUTTON_DANGER_GHOST[1];

    case 'primary_ghost':
      return theme.button.COLORS_BUTTON_PRIMARY_GHOST[1];

    case 'default':
      return theme.button.COLORS_BUTTON_SECONDARY[1];

    case 'tertiary':
      return theme.button.COLORS_BUTTON_TERTIARY[1];

    case 'ghost':
      return theme.button.COLORS_BUTTON_GHOST[1];

    case 'primary':
      return theme.button.COLORS_BUTTON_PRIMARY[1];

    default:
      return theme.button.COLORS_BUTTON_SECONDARY[1];
  }
};

const getColorsActive = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
      return theme.button.COLORS_BUTTON_DANGER[2];

    case 'danger_tertiary':
      return theme.button.COLORS_BUTTON_DANGER_TERTIARY[2];

    case 'danger_ghost':
      return theme.button.COLORS_BUTTON_DANGER_GHOST[2];

    case 'primary_ghost':
      return theme.button.COLORS_BUTTON_PRIMARY_GHOST[2];

    case 'default':
      return theme.button.COLORS_BUTTON_SECONDARY[2];

    case 'tertiary':
      return theme.button.COLORS_BUTTON_GHOST[2];

    case 'ghost':
      return theme.button.COLORS_BUTTON_TERTIARY[2];

    case 'primary':
      return theme.button.COLORS_BUTTON_PRIMARY[2];

    default:
      return theme.button.COLORS_BUTTON_SECONDARY[2];
  }
};

const getColorsLoading = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
      return theme.button.COLORS_BUTTON_DANGER[3];

    case 'danger_tertiary':
      return theme.button.COLORS_BUTTON_DANGER_TERTIARY[3];

    case 'danger_ghost':
      return theme.button.COLORS_BUTTON_DANGER_GHOST[3];

    case 'primary_ghost':
      return theme.button.COLORS_BUTTON_PRIMARY_GHOST[3];

    case 'default':
      return theme.button.COLORS_BUTTON_SECONDARY[3];

    case 'tertiary':
      return theme.button.COLORS_BUTTON_TERTIARY[3];

    case 'ghost':
      return theme.button.COLORS_BUTTON_GHOST[3];

    case 'primary':
      return theme.button.COLORS_BUTTON_PRIMARY[3];

    default:
      return theme.button.COLORS_BUTTON_SECONDARY[3];
  }
};

const getColorsDisabled = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
      return theme.button.COLORS_BUTTON_DANGER[4];

    case 'danger_tertiary':
      return theme.button.COLORS_BUTTON_DANGER_TERTIARY[4];

    case 'danger_ghost':
      return theme.button.COLORS_BUTTON_DANGER_GHOST[4];

    case 'primary_ghost':
      return theme.button.COLORS_BUTTON_PRIMARY_GHOST[4];

    case 'default':
      return theme.button.COLORS_BUTTON_SECONDARY[4];

    case 'tertiary':
      return theme.button.COLORS_BUTTON_TERTIARY[4];

    case 'ghost':
      return theme.button.COLORS_BUTTON_GHOST[4];

    case 'primary':
      return theme.button.COLORS_BUTTON_PRIMARY[4];

    default:
      return theme.button.COLORS_BUTTON_SECONDARY[4];
  }
};

const getBorderWidth = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
    case 'danger_tertiary':
    case 'danger_ghost':
    case 'primary_ghost':
      return theme.button.DANGER_BORDER_WIDTH;

    case 'default':
      return theme.button.SECONDARY_BORDER_WIDTH;

    case 'tertiary':
    case 'ghost':
      return theme.button.TERTIARY_BORDER_WIDTH;

    case 'primary':
      return theme.button.PRIMARY_BORDER_WIDTH;

    default:
      return theme.button.SECONDARY_BORDER_WIDTH;
  }
};

const getPaddings = (color: ButtonColor, theme: ThemeType) => {
  switch (color) {
    case 'danger':
    case 'danger_tertiary':
    case 'danger_ghost':
    case 'primary_ghost':
      return theme.button.DANGER_PADDING;

    case 'default':
      return theme.button.SECONDARY_PADDING;

    case 'tertiary':
    case 'ghost':
      return theme.button.TERTIARY_PADDING;

    case 'primary':
      return theme.button.PRIMARY_PADDING;

    default:
      return theme.button.SECONDARY_PADDING;
  }
};

const getPadding = (color: ButtonColor, size: ButtonSize, theme: ThemeType) => {
  const paddings = getPaddings(color, theme);

  switch (size) {
    case 'large':
      return paddings[2];

    case 'small':
      return paddings[0];

    default:
      return paddings[1];
  }
};

const getHoverTextDecoration = (color: ButtonColor) => {
  switch (color) {
    case 'primary_ghost':
      return 'underline';
    default:
      return 'none';
  }
};

const getTextDecoration = (color: ButtonColor, isPressed: boolean) => {
  switch (color) {
    case 'primary_ghost':
      return isPressed ? 'underline' : 'none';
    default:
      return 'none';
  }
};

export const StyledButton = styled.button<StyledButtonProps>(({
  $color = buttonColors.DEFAULT,
  $size = buttonSizes.DEFAULT,
  disabled = false,
  $isLoading = false,
  $isPressed = false,
  $isFlatLeft = false,
  $isFlatRight = false,
  $isOnlyIcon = false,

  theme,
  theme: {
    fonts,
    input,
    button,
  },
}) => {
  const isChange = !disabled && !$isLoading && !$isPressed;

  const font = $size === buttonSizes.SMALL
    ? fonts[button.FONT_SMALL]
    : fonts[button.FONT];

  const borderWidth = getBorderWidth($color, theme);
  const padding = getPadding($color, $size, theme);

  const colorsDefault = $isPressed
    ? getColorsActive($color, theme)
    : $isLoading
      ? getColorsLoading($color, theme)
      : disabled
        ? getColorsDisabled($color, theme)
        : getColors($color, theme);
  const colorsHover = getColorsHover($color, theme);
  const colorsActive = getColorsActive($color, theme);

  const minHeight = getMinHeight($size, theme);

  return {
    ...font,
    lineHeight: 1,
    position: 'relative',
    boxSizing: 'border-box',
    minHeight,
    minWidth: $isOnlyIcon ? minHeight : undefined,
    display: 'inline-block',
    padding: $isOnlyIcon ? undefined : padding,
    borderWidth,
    borderStyle: 'solid',
    borderColor: colorsDefault[2],
    color: colorsDefault[1],
    backgroundColor: colorsDefault[0],
    outline: 'none',
    cursor: 'pointer',
    textDecoration: getTextDecoration($color, $isPressed),
    userSelect: 'none',
    borderTopLeftRadius: $isFlatLeft ? 0 : input.BORDER_RADIUS,
    borderBottomLeftRadius: $isFlatLeft ? 0 : input.BORDER_RADIUS,
    borderTopRightRadius: $isFlatRight ? 0 : input.BORDER_RADIUS,
    borderBottomRightRadius: $isFlatRight ? 0 : input.BORDER_RADIUS,

    ':hover': isChange ? {
      borderColor: colorsHover[2],
      color: colorsHover[1],
      backgroundColor: colorsHover[0],
      textDecoration: getHoverTextDecoration($color),
    } : undefined,

    ':active': isChange ? {
      borderColor: colorsActive[2],
      color: colorsActive[1],
      backgroundColor: colorsActive[0],
    } : undefined,
  };
});

export const StyledLoader = styled.div(({
  theme: {
    colors,
  },
}) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  fontSize: 16,
  color: colors.ACTION_DEFAULT,
  transform: 'translateX(-50%) translateY(-50%)',
}));

export const ButtonIcon = styled.span<{
  isLeft?: boolean;
  isRight?: boolean;
}>(({
  isLeft,
  isRight,
  theme: { button },
}) => ({
  display: 'inline-block',
  verticalAlign: -5,
  width: button.BUTTON_ICON_WIDTH,
  height: button.BUTTON_ICON_HEIGHT,
  marginTop: button.BUTTON_ICON_MARGIN_TOP,
  marginBottom: button.BUTTON_ICON_MARGIN_BOTTOM,
  marginLeft: isLeft ? -4 : 0,
  marginRight: isRight ? -4 : 0,
}));
